import { KeyboardEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog } from '@/components-new/Dialog/Dialog';
import { DialogButtons } from '@/components-new/Dialog/styles';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { Loader } from '@/components/Loader/Loader';
import { Notification } from '@/components/Notification/Notification';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { formatDate, FULL_DATE_TIME_FORMAT } from '@/helpers/dateHelpers/dateHelpers';
import { TaskActionType } from '@/types/dashboardTypes';
import { DASHBOARD_QUERY_KEYS, loadTask, loadTeam, runTaskAction } from '../../api/api';
import { TIMELINE_LABEL, TIMELINE_STATUS_MAP } from '../../const/const';
import { ReplyRow } from '../../styles';
import { Pills } from '@/components/Pills/Pills';
import { Pill } from '@/components/Pills/styles';
import { StatusWrapper } from '@/components/StatusWrapper/StatusWrapper';
import { TaskFacts } from './ui/TaskFacts/TaskFacts';
import { TextArea } from '@/components/form/fields/TextArea/TextArea';
import { TasksChat } from './ui/TasksChat/TasksChat';

type TaskModalType = {
  isOpen: boolean;
  taskId: string;
  currentUserId?: string;
  onClose: () => void;
  onChanged: () => void;
};

export const TaskModal = ({ isOpen, taskId, currentUserId, onClose, onChanged }: TaskModalType) => {
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');
  const [reassignTo, setReassignTo] = useState<string | null>(null); // null: picker closed
  const [error, setError] = useState('');

  const { data, isPending } = useQuery({
    queryKey: [DASHBOARD_QUERY_KEYS.task, taskId],
    queryFn: () => loadTask(taskId),
  });
  const { data: teamResponse } = useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.team,
    queryFn: loadTeam,
    enabled: reassignTo !== null,
  });
  const task = data?.body;
  const team = (teamResponse?.body?.results ?? []).filter((m) => m.id !== task?.assigned_to);

  const { mutate, isPending: isActing } = useMutation({
    mutationKey: ['dashboard_task_action', taskId],
    mutationFn: ({
      action,
      text,
      assigned_to_id,
    }: {
      action: TaskActionType;
      text?: string;
      assigned_to_id?: string;
    }) => runTaskAction(taskId, action, { text, assigned_to_id }),
    onSuccess(response, { action }) {
      if (response?.error) return setError(getResponseError(response.error));

      setError('');
      setReply('');
      setReassignTo(null);
      queryClient.setQueryData([DASHBOARD_QUERY_KEYS.task, taskId], response);
      onChanged();

      if (action === 'close') {
        showSnackbar('Task closed.');
        onClose();
      }

      // TODO: 29.5.1 commented for now
      // if (action === 'reply') {
      //   showSnackbar(`Reply sent to ${task?.is_assignee ? task.created_by_label : task?.assigned_to_label}.`);
      // }

      if (action === 'reassign') {
        showSnackbar('Task reassigned.');
      }
    },
  });

  const sendReply = () => {
    if (!canSend) return;

    mutate({ action: 'reply', text: reply.trim() });
  };

  const onReplyKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter (and Ctrl/Cmd+Enter) breaks the line.
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;

    event.preventDefault();
    sendReply();
  };

  const can = (action: TaskActionType) => task?.actions.includes(action);
  const timeline = task?.messages.filter((m) => m.is_timeline) ?? [];
  const thread = task?.messages.filter((m) => !m.is_timeline) ?? [];
  const replyPlaceholder =
    task?.status === 'question' && task.can_manage
      ? `Answer ${task.assigned_to_label.split(' ')[0]}'s question`
      : `Write a message to ${task?.is_assignee ? task.created_by_label : task?.assigned_to_label}`;
  const canSend = !!reply.trim() && !isActing;

  return (
    <Dialog isOpen={isOpen} header={task?.title ?? 'Task'} width='760px' onClose={onClose}>
      {isPending || !task ? (
        <Loader />
      ) : (
        <Box display='flex' flexDirection='column' gap='16px'>
          <TaskFacts task={task} />

          <Box>
            <Typography variant='bodySmall' fontWeight='medium' marginBottom='6px'>
              Timeline
            </Typography>
            <Pills>
              {timeline.map((item) => (
                <StatusWrapper key={item.id} variant={TIMELINE_STATUS_MAP[item.kind]}>
                  {TIMELINE_LABEL[item.kind]} · {formatDate(item.date_created, FULL_DATE_TIME_FORMAT)}
                </StatusWrapper>
              ))}
            </Pills>
          </Box>
          <Box border='1px solid #DCE3E0' borderRadius='12px' overflow='hidden'>
            <TasksChat thread={thread} currentUserId={currentUserId} />
            {can('reply') && (
              <ReplyRow>
                <TextArea
                  hasErrorComponent={false}
                  name='reply'
                  rows={1}
                  placeholder={`${replyPlaceholder}`}
                  value={reply}
                  onChange={(event) => setReply(event.target.value)}
                  onKeyDown={onReplyKeyDown}
                />
                <ButtonNew size='large' isLoading={isActing} disabled={!canSend} onClick={sendReply}>
                  Reply
                </ButtonNew>
              </ReplyRow>
            )}
          </Box>
          {error && <Notification variant='error'>{error}</Notification>}
          {reassignTo !== null && (
            <Box>
              <Typography variant='bodySmall' fontWeight='medium' marginBottom='6px'>
                Reassign to
              </Typography>
              <Pills>
                {team.map((member) => (
                  <Pill
                    key={member.id}
                    type='button'
                    $active={reassignTo === member.id}
                    onClick={() => setReassignTo(member.id)}
                  >
                    {member.name}
                  </Pill>
                ))}
              </Pills>
            </Box>
          )}
        </Box>
      )}

      {task && (
        <DialogButtons>
          {can('start') && (
            <ButtonNew variant='secondary' isLoading={isActing} onClick={() => mutate({ action: 'start' })}>
              Start task
            </ButtonNew>
          )}
          {can('ask') && (
            <ButtonNew
              variant='secondary'
              disabled={!reply.trim()}
              isLoading={isActing}
              onClick={() => mutate({ action: 'ask', text: reply })}
            >
              Ask as a question
            </ButtonNew>
          )}
          {can('complete') && (
            <ButtonNew isLoading={isActing} onClick={() => mutate({ action: 'complete', text: reply })}>
              Mark completed
            </ButtonNew>
          )}
          {can('reassign') && reassignTo === null && (
            <ButtonNew variant='secondary' onClick={() => setReassignTo('')}>
              Reassign
            </ButtonNew>
          )}
          {can('reassign') && reassignTo && (
            <ButtonNew
              isLoading={isActing}
              onClick={() => mutate({ action: 'reassign', assigned_to_id: reassignTo, text: reply })}
            >
              Confirm reassign
            </ButtonNew>
          )}
          {can('close') && (
            <ButtonNew isLoading={isActing} onClick={() => mutate({ action: 'close' })}>
              Confirm and close
            </ButtonNew>
          )}
          <ButtonNew variant='secondary' onClick={onClose}>
            Close
          </ButtonNew>
        </DialogButtons>
      )}
    </Dialog>
  );
};
